/** @file ListInput: a comma-separated list field that commits a parsed array on blur or Enter. */
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

/** The list, its label, the item parser (null = invalid item, the draft reverts) and the commit callback. */
export type ListInputProps<T> = { label: string; value: T[]; parse: (item: string) => T | null; onCommit: (value: T[]) => void };

/** Shows `a, b, c`; commits only when every item parses and the list changed. */
export function ListInput<T>(props: ListInputProps<T>): ReactElement {
  const shown = props.value.map(String).join(', ');
  const [text, setText] = useState(shown);
  useEffect(() => { setText(shown); }, [shown]);
  const commit = (): void => {
    const items = text.split(',').map((t) => t.trim()).filter((t) => t !== '').map(props.parse);
    if (items.some((i) => i === null)) { setText(shown); return; }
    const next = items.filter((i): i is T => i !== null);
    if (next.map(String).join(', ') !== shown) props.onCommit(next);
  };
  return (
    <form style={{ display: 'inline', flex: 1 }} onSubmit={(e) => { e.preventDefault(); commit(); }}>
      <input aria-label={props.label} value={text} placeholder="(empty)" onChange={(e) => { setText(e.currentTarget.value); }} onBlur={commit}
        style={{ width: '100%', background: 'var(--input-bg, #11131a)', color: 'inherit', border: '1px solid var(--border, #2a2e38)', borderRadius: 3, font: 'inherit' }} />
    </form>
  );
}
